/**
 * @fileoverview Componente del header/navbar del sitio
 * @author Jonatan Bianchi - Bianchi Desarrollo Web
 * @website https://jonatan.bianchi.com.ar
 * @email jonatan@bianchi.com.ar
 * @company Bianchi Desarrollo Web
 * @website https://www.bianchi.com.ar
 * @email info@bianchi.com.ar
 * @copyright 2025 Bianchi Desarrollo Web
 */

import { Component, AfterViewInit, OnDestroy, OnInit, inject, PLATFORM_ID, effect, Signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { RouterLink, Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { ProductosService } from '../services/productos.service';
import { Linea } from '../models/catalog.model';

/**
 * Componente del header
 * Maneja la navegación y el efecto de scroll en el navbar
 */
@Component({
  selector: 'app-header',
  standalone: true,
  imports: [RouterLink],
  templateUrl: './header.component.html',
  styleUrl: './header.component.css'
})
export class HeaderComponent implements AfterViewInit, OnDestroy, OnInit {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private platformId = inject(PLATFORM_ID);
  private productosService = inject(ProductosService);
  private scrollListener?: () => void;
  
  protected lineas: Linea[] = [];
  private lineasSignal: Signal<Linea[]>;

  constructor() {
    this.lineasSignal = this.productosService.getLineasSignal();
    
    effect(() => {
      this.lineas = this.lineasSignal();
    });
  }

  /**
   * Inicializa el componente y carga las líneas
   */
  ngOnInit(): void {
    // Las líneas se cargan automáticamente a través del effect en el constructor
  }

  /**
   * Inicializa el componente y configura los event listeners
   */
  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.cerrarMenuEnClick();
      this.setupDropdownBehavior();
      this.router.events.subscribe(event => {
        if (event instanceof NavigationEnd) {
          this.cerrarMenu();
          // Scroll a fragmento después de la navegación
          if (event.url.includes('#')) {
            const fragment = event.url.split('#')[1];
            setTimeout(() => this.scrollToFragment(fragment), 100);
          }
        }
      });
      this.setupScrollEffect();
    }
  }

  /**
   * Configura el comportamiento del dropdown para que no cierre el menú principal en móvil
   */
  private setupDropdownBehavior(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    
    setTimeout(() => {
      const navCollapse = document.getElementById('mainNav');
      if (!navCollapse) return;

      // Prevenir que el menú se cierre cuando se hace clic en el dropdown toggle o dentro del dropdown
      navCollapse.addEventListener('click', (event) => {
        const target = event.target as HTMLElement;
        const isDropdownToggle = target.classList.contains('dropdown-toggle') || target.closest('.dropdown-toggle');
        const isInsideDropdown = target.closest('.dropdown-menu');
        
        // Si el click es en el dropdown toggle o dentro del dropdown, no cerrar el menú
        if (isDropdownToggle || isInsideDropdown) {
          event.stopPropagation();
        }
      }, true); // Usar capture phase para interceptar antes
    }, 200);
  }

  /**
   * Limpia los event listeners al destruir el componente
   */
  ngOnDestroy(): void {
    if (this.scrollListener && isPlatformBrowser(this.platformId)) {
      window.removeEventListener('scroll', this.scrollListener);
    }
  }

  /**
   * Configura el efecto visual del navbar cuando se hace scroll
   */
  private setupScrollEffect(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const navbar = document.querySelector('.navbar') as HTMLElement;
    if (!navbar) return;

    this.scrollListener = () => {
      if (window.scrollY > 10) {
        navbar.classList.add('scrolled');
      } else {
        navbar.classList.remove('scrolled');
      }
    };

    window.addEventListener('scroll', this.scrollListener, { passive: true });
  }

  /**
   * Cierra el menú hamburguesa de Bootstrap
   */
  cerrarMenu(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    
    const navCollapse = document.getElementById('mainNav');
    if (navCollapse && navCollapse.classList.contains('show')) {
      const bsCollapse = (window as any).bootstrap?.Collapse?.getInstance(navCollapse);
      if (bsCollapse) {
        bsCollapse.hide();
      } else {
        navCollapse.classList.remove('show');
        const toggler = document.querySelector('[data-bs-target="#mainNav"]') as HTMLElement;
        if (toggler) {
          toggler.setAttribute('aria-expanded', 'false');
        }
      }
    }
  }

  /**
   * Agrega event listeners a los enlaces del menú para cerrarlo al hacer clic
   * Excluye el dropdown toggle para que no cierre el menú al abrir el dropdown
   */
  private cerrarMenuEnClick(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    
    setTimeout(() => {
      // Solo cerrar el menú con los links normales y botones, NO con el dropdown toggle
      const navLinks = document.querySelectorAll('#mainNav .nav-link:not(.dropdown-toggle), #mainNav .btn');
      navLinks.forEach(link => {
        // Excluir el dropdown toggle
        if (!link.classList.contains('dropdown-toggle')) {
          link.addEventListener('click', () => {
            this.cerrarMenu();
          });
        }
      });
      
      // Los dropdown-items sí deben cerrar el menú
      const dropdownItems = document.querySelectorAll('#mainNav .dropdown-item');
      dropdownItems.forEach(item => {
        item.addEventListener('click', () => {
          this.cerrarMenu();
        });
      });
    }, 100);
  }

  /**
   * Método público para cerrar el menú desde el template
   */
  onClickLink(): void {
    this.cerrarMenu();
  }

  /**
   * Maneja el toggle del dropdown sin cerrar el menú principal en móvil
   */
  handleDropdownToggle(event: Event): void {
    if (!isPlatformBrowser(this.platformId)) return;
    
    // En móvil, prevenir que el click cierre el menú principal
    const isMobile = window.innerWidth < 992;
    if (isMobile) {
      // Detener la propagación para que no cierre el menú principal
      event.stopPropagation();
      // Permitir que Bootstrap maneje el dropdown normalmente
    }
  }

  /**
   * Navega a un fragmento en la página
   * Si estamos en la página principal, hace scroll directamente
   * Si estamos en otra página, navega primero a "/" y luego al fragmento
   */
  navigateToFragment(fragment: string, event?: Event): void {
    if (event) {
      event.preventDefault();
    }

    if (!isPlatformBrowser(this.platformId)) return;

    const currentUrl = this.router.url.split('#')[0];
    
    if (currentUrl === '/') {
      // Ya estamos en la página principal, hacer scroll al fragmento
      this.scrollToFragment(fragment);
      this.cerrarMenu();
    } else {
      // Estamos en otra página, navegar primero a "/" y luego al fragmento
      this.router.navigate(['/'], { fragment: fragment }).then(() => {
        setTimeout(() => this.scrollToFragment(fragment), 100);
        this.cerrarMenu();
      });
    }
  }

  /**
   * Hace scroll suave al elemento con el ID del fragmento
   */
  private scrollToFragment(fragment: string): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const element = document.getElementById(fragment);
    if (element) {
      const offset = 80; // Altura del navbar fixed
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  }

  /**
   * Remueve el prefijo "Linea " del nombre de la línea
   */
  protected getNombreSinLinea(nombre: string): string {
    return nombre.replace(/^Linea\s+/i, '');
  }
}
